import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { api, queryKeys } from "../../api";
import { OrgLayout } from "../../components/Shell";
import { AutoLoadMore, DataTable, PageHeader } from "../../components/ui";
import { ErrorState } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { deploymentAuditActions, deploymentAuditLabels } from "@backstory/deployment";

const ACTIONS = ["", "auth.login", "auth.logout", "auth.break_glass", "auth.ip_bypass", "session.view", "session.reviewed", "session.unreviewed", "session.export", "session.share", "member.invite", "member.role_change", "member.remove", "project.update", "project.rotate_key", "token.create", "token.revoke", "sso.update", "privacy.unmask", "live.control_start", ...deploymentAuditActions];

const ACTION_LABELS: Record<string, string> = {
  "auth.login": "Sign in",
  "auth.logout": "Sign out",
  "auth.break_glass": "Break-glass sign-in",
  "auth.ip_bypass": "Emergency network sign-in",
  "session.view": "View session",
  "session.reviewed": "Mark reviewed",
  "session.unreviewed": "Clear reviewed",
  "session.export": "Export session",
  "session.share": "Share session",
  "member.invite": "Invite member",
  "member.role_change": "Change role",
  "member.remove": "Remove member",
  "project.update": "Update app",
  "project.rotate_key": "Rotate key",
  "token.create": "Create token",
  "token.revoke": "Revoke token",
  "sso.update": "Update single sign-on",
  "privacy.unmask": "Unmask",
  "live.control_start": "Start live assist",
  ...deploymentAuditLabels,
};

function actionLabel(id: string): string {
  if (!id) return "All actions";
  if (ACTION_LABELS[id]) return ACTION_LABELS[id];
  return id.split(".").map((part) => part.replaceAll("_", " ")).join(" · ");
}

export function AuditPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const filters = useMemo(() => ({ action: action || undefined, actor: actor || undefined }), [action, actor]);
  const q = useInfiniteQuery({
    queryKey: queryKeys.audit(orgId, filters),
    queryFn: ({ pageParam }) => api.orgs.audit(orgId, { ...filters, cursor: pageParam as string | undefined, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Audit log">
        <div className="toolbar audit-filters">
          <label className="muted small" style={{ display: "grid", gap: 4 }}>
            Action
            <select className="input" value={action} onChange={(e) => setAction(e.target.value)} style={{ minWidth: 200 }} autoComplete="off">
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {actionLabel(a)}
                </option>
              ))}
            </select>
          </label>
          <label className="muted small" style={{ display: "grid", gap: 4 }}>
            Actor
            <input className="input" placeholder="email or token id" value={actor} onChange={(e) => setActor(e.target.value)} autoComplete="off" />
          </label>
        </div>
      </PageHeader>
      <div className="card stack-table">
        {q.isError ? (
          <ErrorState error={q.error} retry={() => void q.refetch()} />
        ) : (
        <>
        <DataTable
          ariaLabel="Audit entries"
          rows={rows}
          loading={q.isPending || q.isFetchingNextPage}
          rowKey={(e) => e.id}
          empty="No audit entries match."
          columns={[
            { key: "at", header: "When", label: "When", render: (e) => <span title={e.at}>{formatDateTime(e.at)}</span>, width: 150 },
            { key: "actor", header: "Actor", label: "Actor", render: (e) => (<><span className="badge neutral">{e.actor.type}</span> {e.actor.email ?? e.actor.id}</>) },
            { key: "action", header: "Action", label: "Action", render: (e) => actionLabel(e.action) },
            { key: "target", header: "Target", label: "Target", render: (e) => (e.target ? <span className="mono small">{e.target.type}:{e.target.id}</span> : <span className="muted">—</span>) },
            { key: "ip", header: "IP", label: "IP", render: (e) => <span className="mono small">{e.ip ?? ""}</span> },
            { key: "meta", header: "Details", label: "Details", render: (e) => (e.meta ? <details><summary className="muted small">Details</summary><pre className="mono small">{JSON.stringify(e.meta, null, 1)}</pre></details> : null) },
          ]}
        />
        <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more audit entries" />
        </>
        )}
      </div>
    </OrgLayout>
  );
}
